/**
 * Tabbed content panels.
 *
 * Follows the ARIA tabs pattern: one tab in the tab order at a time, arrow keys
 * move between them, Home/End jump to the ends. Panels are hidden server-side
 * except the first, so the correct one is visible before this script runs.
 */
class TabContentComponent extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    if (this.tabs.length < 2) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab));
      tab.addEventListener('keydown', (event) => this.onKeydown(event));
    });
  }

  panelFor(tab) {
    return this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
  }

  select(tab, { focus = false } = {}) {
    this.tabs.forEach((candidate) => {
      const isTarget = candidate === tab;
      candidate.setAttribute('aria-selected', isTarget ? 'true' : 'false');
      candidate.setAttribute('tabindex', isTarget ? '0' : '-1');
      candidate.classList.toggle('tab-content__tab--active', isTarget);

      const panel = this.panelFor(candidate);
      if (panel) panel.hidden = !isTarget;
    });
    if (focus) tab.focus();
  }

  onKeydown(event) {
    const index = this.tabs.indexOf(event.target);
    if (index === -1) return;

    let next = null;
    switch (event.key) {
      case 'ArrowRight':
        next = this.tabs[(index + 1) % this.tabs.length];
        break;
      case 'ArrowLeft':
        next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
        break;
      case 'Home':
        next = this.tabs[0];
        break;
      case 'End':
        next = this.tabs[this.tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next, { focus: true });
  }
}

if (!customElements.get('tab-content-component')) {
  customElements.define('tab-content-component', TabContentComponent);
}
